<template>
  <v-container fluid class="pa-4">
    <!-- Section Quotes-parts Salariales -->
    <v-card elevation="2">
      <v-card-title class="white--text d-flex justify-space-between align-center" style="background-color: #607D8B;">
        <div>
          <v-icon left dark>mdi-account-multiple</v-icon>
          Quotes-parts Salariales - Tous les Employes
        </div>
        <div class="d-flex">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="exportToExcel"
                  :loading="exportLoading"
                  class="mr-2"
                  :disabled="employeesData.length === 0"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-file-excel-outline</v-icon>
              </v-btn>
            </template>
            <span>Exporter en Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="refreshData"
                  :loading="loading"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Actualiser les données</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-alert color="info" text dense class="mb-4">
          <v-icon left small>mdi-information</v-icon>
          Retrouvez ici les quotes-parts salariales de tous les employes de l'entreprise pour le mois en cours et le mois precedent.
        </v-alert>

        <!-- Statistiques générales -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3" style="border-top: 4px solid #2196F3;">
              <v-icon size="48" color="blue">mdi-account-group</v-icon>
              <h2 class="mt-3">{{ employeesData.length }}</h2>
              <p class="text-subtitle-2 grey--text">Nombre d'Employes</p>
              <p class="text-caption grey--text">Total dans l'entreprise</p>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
              <v-icon size="48" color="orange">mdi-calculator</v-icon>
              <h2 class="mt-3">{{ formatNumber(totalQuotePartActuelle) }}</h2>
              <p class="text-subtitle-2 grey--text">Total Mois Actuel</p>
              <p class="text-caption grey--text">Somme des quotes-parts</p>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
              <v-icon size="48" color="green">mdi-calendar-minus</v-icon>
              <h2 class="mt-3">{{ formatNumber(totalQuotePartPrecedente) }}</h2>
              <p class="text-subtitle-2 grey--text">Total Mois Precedent</p>
              <p class="text-caption grey--text">Somme des quotes-parts</p>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="text-center pa-3" style="border-top: 4px solid #9C27B0;">
              <v-icon size="48" color="purple">mdi-trending-up</v-icon>
              <h2 class="mt-3">{{ formatNumber(evolutionQuotePart) }}</h2>
              <p class="text-subtitle-2 grey--text">Evolution</p>
              <p class="text-caption grey--text">Difference entre les mois</p>
            </v-card>
          </v-col>
        </v-row>

        <!-- Boutons de sélection rapide -->
        <v-row class="mb-3">
          <v-col cols="12" class="text-center">
            <v-btn-toggle
                v-model="selectedView"
                mandatory
                color="primary"
                dense
                class="flex-wrap justify-center"
            >
              <v-btn value="all" small>
                <v-icon left small>mdi-account-multiple</v-icon>
                Tous les employes
              </v-btn>
              <v-btn value="current" small>
                <v-icon left small>mdi-calendar-month</v-icon>
                Mois actuel &gt; 0
              </v-btn>
              <v-btn value="previous" small>
                <v-icon left small>mdi-calendar-minus</v-icon>
                Mois precedent &gt; 0
              </v-btn>
              <v-btn value="evolution" small>
                <v-icon left small>mdi-chart-line</v-icon>
                Avec evolution
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <!-- Filtres -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6">
            <v-text-field
                v-model="searchQuery"
                label="Rechercher un employé..."
                prepend-icon="mdi-magnify"
                outlined
                dense
                clearable
                @click:clear="clearSearch"
                placeholder="Nom, prénom ou matricule"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
                v-model="sortBy"
                :items="sortOptions"
                label="Trier par"
                prepend-icon="mdi-sort"
                outlined
                dense
                @change="applySorting"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Tableau -->
        <v-data-table
            :headers="headers"
            :items="filteredEmployees"
            :items-per-page="15"
            class="elevation-1"
            :loading="loading"
            loading-text="Chargement des données..."
            disable-sort
        >
          <template v-slot:[`item.nom`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.matricule)">
                <span class="white--text text-caption font-weight-bold">
                  {{ getInitials(item.nom, item.prenom) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ item.nom }} {{ item.prenom }}</div>
                <div class="text-caption grey--text">{{ item.matricule }}</div>
              </div>
            </div>
          </template>

          <template v-slot:[`item.quotepartMoisEnCours`]="{ item }">
            <div class="text-right">
              <v-chip
                  small
                  :color="getQuotePartColor(item.quotepartMoisEnCours)"
                  :dark="item.quotepartMoisEnCours > 0"
                  :outlined="item.quotepartMoisEnCours === 0"
              >
                {{ formatNumber(item.quotepartMoisEnCours) }} FCFA
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.quotepartMoisPrecedent`]="{ item }">
            <div class="text-right">
              <v-chip
                  small
                  :color="getQuotePartColor(item.quotepartMoisPrecedent)"
                  :dark="item.quotepartMoisPrecedent > 0"
                  :outlined="item.quotepartMoisPrecedent === 0"
              >
                {{ formatNumber(item.quotepartMoisPrecedent) }} FCFA
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.evolution`]="{ item }">
            <div class="text-center">
              <v-chip
                  small
                  :color="getEvolutionColor(item.evolution)"
                  dark
                  v-if="item.evolution !== 0"
              >
                <v-icon left small>
                  {{ getEvolutionIcon(item.evolution) }}
                </v-icon>
                {{ formatNumber(Math.abs(item.evolution)) }} FCFA
              </v-chip>
              <v-chip
                  small
                  color="grey"
                  outlined
                  v-else
              >
                <v-icon left small>mdi-minus</v-icon>
                Aucune évolution
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
                icon
                small
                @click="viewEmployeeDetails(item)"
                color="primary"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
                icon
                small
                @click="viewEmployeeHistory(item)"
                color="orange"
            >
              <v-icon small>mdi-history</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Ligne de total séparée -->
        <v-card outlined class="mt-3">
          <v-card-text class="pa-3">
            <v-row align="center" class="font-weight-bold">
              <v-col cols="12" md="3" class="text-center">
                <div class="text-h6 primary--text">TOTAL GÉNÉRAL</div>
              </v-col>
              <v-col cols="12" md="3" class="text-center">
                <v-chip color="orange" dark large>
                  <v-icon left>mdi-calendar-month</v-icon>
                  {{ formatNumber(totalQuotePartActuelle) }} FCFA
                </v-chip>
                <div class="text-caption grey--text mt-1">Quote-part Actuelle</div>
              </v-col>
              <v-col cols="12" md="3" class="text-center">
                <v-chip color="green" dark large>
                  <v-icon left>mdi-calendar-minus</v-icon>
                  {{ formatNumber(totalQuotePartPrecedente) }} FCFA
                </v-chip>
                <div class="text-caption grey--text mt-1">Quote-part Précédente</div>
              </v-col>
              <v-col cols="12" md="3" class="text-center">
                <v-chip :color="getEvolutionColor(evolutionQuotePart)" dark large>
                  <v-icon left>
                    {{ getEvolutionIcon(evolutionQuotePart) }}
                  </v-icon>
                  {{ formatNumber(Math.abs(evolutionQuotePart)) }} FCFA
                </v-chip>
                <div class="text-caption grey--text mt-1">
                  {{ getEvolutionText(evolutionQuotePart) }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Dialog pour les détails d'un employé -->
    <v-dialog v-model="detailsDialog" max-width="600">
      <v-card>
        <v-card-title class="headline white--text" style="background-color: #03A9F4;">
          <v-icon left dark>mdi-account-details</v-icon>
          Détails de l'employé
        </v-card-title>
        <v-card-text class="pt-4" v-if="selectedEmployee">
          <v-row>
            <v-col cols="12" class="text-center">
              <v-avatar size="80" :color="getEmployeeColor(selectedEmployee.matricule)" class="mb-3">
                <span class="white--text text-h5 font-weight-bold">
                  {{ getInitials(selectedEmployee.nom, selectedEmployee.prenom) }}
                </span>
              </v-avatar>
              <h2>{{ selectedEmployee.nom }} {{ selectedEmployee.prenom }}</h2>
              <p class="text-subtitle-1 grey--text">Matricule: {{ selectedEmployee.matricule }}</p>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row>
            <v-col cols="6">
              <v-card outlined class="pa-3 text-center">
                <v-icon size="40" color="orange">mdi-calendar-month</v-icon>
                <h3 class="mt-2">{{ formatNumber(selectedEmployee.quotepartMoisEnCours) }} FCFA</h3>
                <p class="text-caption grey--text">Quote-part Mois Actuel</p>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card outlined class="pa-3 text-center">
                <v-icon size="40" color="green">mdi-calendar-minus</v-icon>
                <h3 class="mt-2">{{ formatNumber(selectedEmployee.quotepartMoisPrecedent) }} FCFA</h3>
                <p class="text-caption grey--text">Quote-part Mois Précédent</p>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-3">
            <v-col cols="12">
              <v-card outlined class="pa-3 text-center">
                <v-icon size="40" :color="getEvolutionColor(selectedEmployee.evolution)">
                  {{ getEvolutionIcon(selectedEmployee.evolution) }}
                </v-icon>
                <h3 class="mt-2">{{ formatNumber(Math.abs(selectedEmployee.evolution)) }} FCFA</h3>
                <p class="text-caption grey--text">
                  {{ getEvolutionText(selectedEmployee.evolution) }}
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog pour les alertes -->
    <v-dialog v-model="alertDialog" max-width="500">
      <v-card>
        <v-card-title :class="alertColor === 'success' ? 'success' : 'error'" class="white--text">
          {{ alertTitle }}
        </v-card-title>
        <v-card-text class="pt-4">
          {{ alertMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="alertDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading overlay -->
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js";
import * as XLSX from "xlsx";

export default {
  name: "QuotesPartsSalariales",
  data() {
    return {
      loading: false,
      exportLoading: false,
      alertDialog: false,
      alertTitle: "",
      alertMessage: "",
      alertColor: "info",
      detailsDialog: false,
      selectedEmployee: null,

      // Données des employés
      employeesData: [],

      // Filtres et tri
      searchQuery: "",
      selectedView: "all",
      sortBy: "nom",

      // Options de tri
      sortOptions: [
        { text: "Nom (A-Z)", value: "nom" },
        { text: "Nom (Z-A)", value: "nom_desc" },
        { text: "Matricule", value: "matricule" },
        { text: "Quote-part actuelle (croissant)", value: "quotepartMoisEnCours" },
        { text: "Quote-part actuelle (décroissant)", value: "quotepartMoisEnCours_desc" },
        { text: "Quote-part précédente (croissant)", value: "quotepartMoisPrecedent" },
        { text: "Quote-part précédente (décroissant)", value: "quotepartMoisPrecedent_desc" },
        { text: "Evolution (croissant)", value: "evolution" },
        { text: "Evolution (décroissant)", value: "evolution_desc" }
      ],

      // Headers du tableau
      headers: [
        { text: "Employee", value: "nom", align: "start", sortable: true },
        { text: "Matricule", value: "matricule", align: "center", sortable: true },
        { text: "Quote-part Mois Actuel", value: "quotepartMoisEnCours", align: "end", sortable: true },
        { text: "Quote-part Mois Precedent", value: "quotepartMoisPrecedent", align: "end", sortable: true },
        { text: "Evolution", value: "evolution", align: "center", sortable: true },
        { text: "Actions", value: "actions", align: "center", sortable: false }
      ]
    };
  },

  computed: {
    totalQuotePartActuelle() {
      return this.employeesData.reduce((sum, emp) => sum + (emp.quotepartMoisEnCours || 0), 0);
    },

    totalQuotePartPrecedente() {
      return this.employeesData.reduce((sum, emp) => sum + (emp.quotepartMoisPrecedent || 0), 0);
    },

    evolutionQuotePart() {
      return this.totalQuotePartActuelle - this.totalQuotePartPrecedente;
    },

    filteredEmployees() {
      let filtered = this.employeesData.map(emp => ({
        ...emp,
        evolution: (emp.quotepartMoisEnCours || 0) - (emp.quotepartMoisPrecedent || 0)
      }));

      // Filtrage par vue sélectionnée
      switch (this.selectedView) {
        case "current":
          filtered = filtered.filter(emp => emp.quotepartMoisEnCours > 0);
          break;
        case "previous":
          filtered = filtered.filter(emp => emp.quotepartMoisPrecedent > 0);
          break;
        case "evolution":
          filtered = filtered.filter(emp => emp.evolution !== 0);
          break;
        default:
          // "all" - pas de filtrage supplémentaire
          break;
      }

      // Filtrage par recherche
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(emp =>
            emp.nom.toLowerCase().includes(query) ||
            emp.prenom.toLowerCase().includes(query) ||
            emp.matricule.toLowerCase().includes(query)
        );
      }

      // Tri personnalisé
      if (this.sortBy && this.sortBy !== 'nom') {
        filtered = this.applySortToArray(filtered, this.sortBy);
      }

      return filtered;
    }
  },

  created() {
    this.loadData();
  },

  methods: {
    async refreshData() {
      this.searchQuery = "";
      this.selectedView = "all";
      this.sortBy = "nom";

      await this.loadData();
      this.showAlert("success", "Actualisation réussie", "Les données ont été mises à jour");
      this.$vuetify.goTo(0);
    },

    async loadData() {
      this.loading = true;

      try {
        await this.getQuotesPartsSalariales();
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        this.showAlert("error", "Erreur de chargement", "Impossible de charger les données");
      } finally {
        this.loading = false;
      }
    },

    async getQuotesPartsSalariales() {
      const url = `${this.$store.getters.user.baseUrl}/wallet/get_quote_part_salariale_for_all_employees/${sessionStorage.getItem("companyId")}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);

        if (response.data.status === 200) {
          this.employeesData = response.data.datas || [];

          if (this.employeesData.length === 0) {
            this.showAlert("info", "Aucune donnée", "Aucun employé trouvé ou aucune quote-part calculée");
          }
        } else {
          this.showAlert("warning", "Avertissement", response.data.message || "Erreur lors du chargement");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des quotes-parts:", error);
        this.showAlert("error", "Erreur", "Erreur lors de la communication avec le serveur");
      }
    },

    clearSearch() {
      this.searchQuery = "";
    },

    applySorting() {
      // Cette méthode sera appelée quand l'utilisateur change le tri
      // Le tri est maintenant géré dans le computed filteredEmployees
    },

    applySortToArray(array, sortBy) {
      const sorted = [...array];

      switch (sortBy) {
        case 'nom':
          return sorted.sort((a, b) => a.nom.localeCompare(b.nom));
        case 'nom_desc':
          return sorted.sort((a, b) => b.nom.localeCompare(a.nom));
        case 'matricule':
          return sorted.sort((a, b) => a.matricule.localeCompare(b.matricule));
        case 'quotepartMoisEnCours':
          return sorted.sort((a, b) => (a.quotepartMoisEnCours || 0) - (b.quotepartMoisEnCours || 0));
        case 'quotepartMoisEnCours_desc':
          return sorted.sort((a, b) => (b.quotepartMoisEnCours || 0) - (a.quotepartMoisEnCours || 0));
        case 'quotepartMoisPrecedent':
          return sorted.sort((a, b) => (a.quotepartMoisPrecedent || 0) - (b.quotepartMoisPrecedent || 0));
        case 'quotepartMoisPrecedent_desc':
          return sorted.sort((a, b) => (b.quotepartMoisPrecedent || 0) - (a.quotepartMoisPrecedent || 0));
        case 'evolution':
          return sorted.sort((a, b) => a.evolution - b.evolution);
        case 'evolution_desc':
          return sorted.sort((a, b) => b.evolution - a.evolution);
        default:
          return sorted.sort((a, b) => a.nom.localeCompare(b.nom));
      }
    },

    viewEmployeeDetails(employee) {
      this.selectedEmployee = {
        ...employee,
        evolution: (employee.quotepartMoisEnCours || 0) - (employee.quotepartMoisPrecedent || 0)
      };
      this.detailsDialog = true;
    },

    viewEmployeeHistory(employee) {
      // Redirection vers la page de détails de l'employé
      this.$router.push({
        name: 'RapportsSalarieAdmin',
        query: {
          matricule: employee.matricule
        }
      });
    },

    formatNumber(value) {
      if (!value || value === 0) return "0";
      return new Intl.NumberFormat('fr-FR').format(value);
    },

    getInitials(nom, prenom) {
      const n = nom ? nom.charAt(0).toUpperCase() : "";
      const p = prenom ? prenom.charAt(0).toUpperCase() : "";
      return n + p;
    },

    getEmployeeColor(matricule) {
      // Générer une couleur basée sur le matricule
      const colors = ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#D32F2F', '#00796B', '#5D4037', '#455A64'];
      const index = matricule ? matricule.length % colors.length : 0;
      return colors[index];
    },

    getQuotePartColor(amount) {
      if (!amount || amount === 0) return "grey";
      if (amount > 50000) return "red";
      if (amount > 25000) return "orange";
      if (amount > 10000) return "amber";
      return "green";
    },

    getEvolutionColor(evolution) {
      if (evolution > 0) return "success";
      if (evolution < 0) return "error";
      return "grey";
    },

    getEvolutionIcon(evolution) {
      if (evolution > 0) return "mdi-trending-up";
      if (evolution < 0) return "mdi-trending-down";
      return "mdi-minus";
    },

    getEvolutionText(evolution) {
      if (evolution > 0) return "Augmentation";
      if (evolution < 0) return "Diminution";
      return "Stable";
    },

    async exportToExcel() {
      this.exportLoading = true;

      try {
        // Préparer les données pour l'export
        const dataToExport = [];

        // En-têtes
        dataToExport.push([
          "Nom",
          "Prenom",
          "Matricule",
          "Quote-part Mois Actuel (FCFA)",
          "Quote-part Mois Precedent (FCFA)",
          "Evolution (FCFA)",
          "Statut Evolution"
        ]);

        // Données des employés filtrés
        this.filteredEmployees.forEach(emp => {
          dataToExport.push([
            emp.nom || '',
            emp.prenom || '',
            emp.matricule || '',
            emp.quotepartMoisEnCours || 0,
            emp.quotepartMoisPrecedent || 0,
            emp.evolution || 0,
            this.getEvolutionText(emp.evolution)
          ]);
        });

        // Ajouter une ligne de totaux
        dataToExport.push([
          "",
          "",
          "TOTAL GENERAL",
          this.totalQuotePartActuelle,
          this.totalQuotePartPrecedente,
          this.evolutionQuotePart,
          this.getEvolutionText(this.evolutionQuotePart)
        ]);

        // Créer le workbook Excel
        const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        // Ajuster la largeur des colonnes
        const columnWidths = [
          { wch: 15 }, // Nom
          { wch: 15 }, // Prenom
          { wch: 12 }, // Matricule
          { wch: 20 }, // Quote-part Actuelle
          { wch: 20 }, // Quote-part Precedente
          { wch: 15 }, // Evolution
          { wch: 15 }  // Statut Evolution
        ];
        worksheet['!cols'] = columnWidths;

        // Ajouter la feuille au workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "Quotes-parts Salariales");

        // Générer le nom du fichier avec la date
        const currentDate = new Date().toISOString().split('T')[0];
        const companyName = sessionStorage.getItem("companyName") || "Entreprise";
        const fileName = `Quotes-parts_Salariales_${companyName}_${currentDate}.xlsx`;

        // Télécharger le fichier
        XLSX.writeFile(workbook, fileName);

        this.showAlert("success", "Export réussi", `Le fichier ${fileName} a été téléchargé avec succès`);

      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        this.showAlert("error", "Erreur d'export", "Impossible d'exporter les données vers Excel");
      } finally {
        this.exportLoading = false;
      }
    },

    showAlert(color, title, message) {
      this.alertColor = color;
      this.alertTitle = title;
      this.alertMessage = message;
      this.alertDialog = true;
    }
  }
};
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.v-data-table {
  font-size: 0.9rem;
}

/* Style pour les en-têtes de colonnes */
.v-data-table >>> thead tr th {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #1976D2 !important;
  background-color: #F5F5F5 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px !important;
}

/* Ajouter une bordure sous les en-têtes */
.v-data-table >>> thead tr {
  border-bottom: 2px solid #E0E0E0;
}

/* Style au survol des en-têtes */
.v-data-table >>> thead tr th:hover {
  background-color: #EEEEEE !important;
  cursor: pointer;
}

/* Icônes de tri plus visibles */
.v-data-table >>> thead tr th .v-icon {
  color: #1976D2 !important;
  font-size: 20px !important;
}

/* Amélioration de l'apparence des cartes */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Style pour les statistiques */
.text-caption {
  font-size: 0.75rem !important;
  color: #666;
}

/* Amélioration du tableau */
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 4px;
}

/* Style pour les boutons de sélection */
.v-btn-toggle {
  flex-wrap: wrap;
  display: inline-flex !important;
}

.v-btn-toggle .v-btn {
  min-width: 140px;
  margin: 2px;
}

/* Centrer le contenu */
.text-center {
  text-align: center;
}

.justify-center {
  justify-content: center;
}

/* Responsive */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1rem;
  }

  h2 {
    font-size: 1.5rem !important;
  }

  /* Ajuster les colonnes sur mobile */
  .v-data-table >>> th:nth-child(4),
  .v-data-table >>> td:nth-child(4) {
    display: none;
  }

  .v-btn-toggle .v-btn {
    min-width: 100px;
    font-size: 0.8rem;
  }
}

/* Animation pour les chips */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}
</style>